/**
 * 🎯 PROGRESO POR SERIE — lógica PURA (sin DB, sin framework), unit-testada.
 *
 * Es la firma de la pantalla "Mi Colección": para cada serie del catálogo,
 * cuántos de sus productos posee el usuario (poseídos / total). El join real
 * (colección local [L] × catalog_series global [H]) lo hace la query reactiva
 * en el dispositivo; aquí sólo vive el cálculo, aislado y determinista.
 *
 * `owned` cuenta PRODUCTOS DISTINTOS poseídos de la serie (no cantidades): tener
 * 3 unidades de un set cuenta como 1 hacia el progreso de completar la serie.
 */

export interface SeriesProgress {
  seriesId: string;
  /** Productos distintos de la serie que el usuario posee. */
  owned: number;
  /** Total de productos de la serie (de `catalog_series.total_items`). */
  total: number;
  /** Entero 0..100 (redondeado). 0 si la serie no tiene total conocido. */
  percent: number;
  /** true cuando owned >= total (y total > 0). */
  complete: boolean;
}

/**
 * Progreso de UNA serie a partir de sus contadores crudos. Robusto ante datos
 * sucios: negativos se saturan a 0, `owned` nunca excede `total` para el
 * porcentaje, y total<=0 ⇒ percent 0 / complete false (evita /0).
 */
export function computeSeriesProgress(
  owned: number,
  total: number,
): Omit<SeriesProgress, "seriesId"> {
  const safeTotal = Number.isFinite(total) && total > 0 ? Math.floor(total) : 0;
  const safeOwnedRaw = Number.isFinite(owned) && owned > 0 ? Math.floor(owned) : 0;
  // El porcentaje se calcula sobre owned acotado a [0, total]; el owned crudo se
  // conserva por si el catálogo va por detrás de la colección (owned > total).
  const cappedOwned = safeTotal > 0 ? Math.min(safeOwnedRaw, safeTotal) : safeOwnedRaw;
  const percent = safeTotal > 0 ? Math.round((cappedOwned / safeTotal) * 100) : 0;
  return {
    owned: safeOwnedRaw,
    total: safeTotal,
    percent,
    complete: safeTotal > 0 && safeOwnedRaw >= safeTotal,
  };
}

/**
 * Progreso de TODAS las series. Combina los totales del catálogo con el mapa de
 * poseídos-por-serie (que produce la query de colección). Determinista: ordena
 * por seriesId para una salida estable.
 */
export function computeAllSeriesProgress(
  seriesTotals: readonly { seriesId: string; total: number }[],
  ownedBySeriesId: Readonly<Record<string, number>>,
): SeriesProgress[] {
  return seriesTotals
    .toSorted((a, b) => a.seriesId.localeCompare(b.seriesId))
    .map(({ seriesId, total }) =>
      Object.assign({ seriesId }, computeSeriesProgress(ownedBySeriesId[seriesId] ?? 0, total)),
    );
}

/**
 * Recuento de la colección por estado — lógica PURA (sin DB), unit-testada.
 * Alimenta las tarjetas de estadística del perfil (reemplaza el recuento de
 * tareas del scaffold listto). `duplicate` y estados desconocidos/null cuentan
 * como poseídos (son piezas en posesión); `wishlist` es intención, no posesión.
 */
export interface CollectionStats {
  /** Ítems poseídos (`owned`, `duplicate`, o estado desconocido). */
  owned: number;
  /** Ítems en lista de deseos (`wishlist`). */
  wishlist: number;
  /** Duplicados (`duplicate`) — subconjunto de `owned`. */
  duplicate: number;
  /** Total de filas de colección (poseídos + deseados). */
  total: number;
}

/** Deriva los recuentos por estado de las filas de colección. Puro. */
export function computeCollectionStats(
  items: readonly { state: string | null }[],
): CollectionStats {
  let owned = 0;
  let wishlist = 0;
  let duplicate = 0;
  for (const it of items) {
    if (it.state === "wishlist") {
      wishlist += 1;
    } else {
      owned += 1;
      if (it.state === "duplicate") {
        duplicate += 1;
      }
    }
  }
  return { owned, wishlist, duplicate, total: items.length };
}

/** Progreso global agregado (para el header de "Mi Colección"). */
export function computeOverallProgress(progresses: readonly SeriesProgress[]): {
  owned: number;
  total: number;
  percent: number;
  completedSeries: number;
} {
  const owned = progresses.reduce((acc, p) => acc + Math.min(p.owned, p.total || p.owned), 0);
  const total = progresses.reduce((acc, p) => acc + p.total, 0);
  return {
    owned,
    total,
    percent: total > 0 ? Math.round((Math.min(owned, total) / total) * 100) : 0,
    completedSeries: progresses.filter((p) => p.complete).length,
  };
}
